/**
 * Shared UI Components
 * Reusable UI components used across the application.
 * 
 * Extracted from App.js for better code organization.
 */

import React from 'react';
import { 
  User, Tags, Clock, TrendingUp, Mail, HelpCircle, 
  CheckCircle, XCircle, AlertCircle 
} from 'lucide-react';

/**
 * Section Header Component
 * Displays a section title with an icon and optional action button.
 */
export const SectionHeader = ({ 
  icon: IconComponent, 
  title, 
  action = null, 
  className = "" 
}) => (
  <div className={`flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 ${className}`}>
    <div className="inline-flex items-center gap-3 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
      <IconComponent 
        size={20} 
        className="text-blue-600" 
        aria-label={`${title} section`}
      />
      <h2 className="text-sm font-semibold text-slate-700 tracking-wide uppercase">{title}</h2>
    </div>
    {action && (
      <div className="text-sm">
        {action}
      </div>
    )}
  </div>
);

/**
 * Enhanced Card Component
 * A styled card container with optional hover effects.
 */
export const EnhancedCard = ({ 
  children, 
  className = "", 
  hover = true 
}) => (
  <div className={`bg-white rounded-xl border border-slate-200 transition-all duration-200 ${
    hover ? 'shadow-sm hover:shadow-md hover:border-slate-300' : 'shadow-sm'
  } ${className}`}>
    {children}
  </div>
);

/**
 * Assignee Chip Component
 * Displays the assigned user with initials avatar.
 */
export const AssigneeChip = ({ assigneeName }) => {
  if (!assigneeName || assigneeName === 'Unassigned') {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-500 rounded-full text-sm">
        <User size={16} className="text-slate-400" />
        <span>Unassigned</span>
      </div>
    );
  }

  // Extract initials from display name
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  const initials = getInitials(assigneeName);

  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
      <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
        {initials}
      </div>
      <span>{assigneeName}</span>
    </div>
  );
};

/**
 * Enhanced Status Badge with Icon
 * Displays status with gradient background and icon.
 */
export const EnhancedStatusBadge = ({ status, orderData }) => {
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return {
          icon: Tags,
          colors: 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border-blue-200',
          label: 'Open'
        };
      case 'pending':
        return {
          icon: Clock,
          colors: 'bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-700 border-yellow-200',
          label: 'Pending'
        };
      case 'in_progress':
      case 'processing':
        return {
          icon: TrendingUp,
          colors: 'bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 border-purple-200',
          label: 'In Progress'
        };
      case 'mail_away':
      case 'mailaway':
        return {
          icon: Mail,
          colors: 'bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700 border-orange-200',
          label: 'Mail Away'
        };
      case 'mail_away_received':
      case 'mailaway_received':
        return {
          icon: Mail,
          colors: 'bg-gradient-to-r from-teal-50 to-teal-100 text-teal-700 border-teal-200',
          label: 'Mail Away Received'
        };
      case 'clarification':
        return {
          icon: HelpCircle,
          colors: 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700 border-amber-200',
          label: 'Clarification'
        };
      case 'clarification_received':
        return {
          icon: CheckCircle,
          colors: 'bg-gradient-to-r from-lime-50 to-lime-100 text-lime-700 border-lime-200',
          label: 'Clarification Received'
        };
      case 'complete':
      case 'completed':
        return {
          icon: CheckCircle,
          colors: 'bg-gradient-to-r from-green-50 to-green-100 text-green-700 border-green-200',
          label: 'Complete'
        };
      case 'cancelled':
      case 'canceled':
        return {
          icon: XCircle,
          colors: 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 border-red-200',
          label: 'Cancelled'
        };
      case 'qc_review':
        return {
          icon: AlertCircle,
          colors: 'bg-gradient-to-r from-indigo-50 to-indigo-100 text-indigo-700 border-indigo-200',
          label: 'QC Review'
        };
      default:
        return {
          icon: Clock,
          colors: 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border-gray-200',
          label: status || 'Unknown'
        };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${config.colors}`}>
      <Icon size={14} />
      <span>{config.label}</span>
    </div>
  );
};

/**
 * Tooltip Component
 * Simple tooltip wrapper using CSS.
 */
export const Tooltip = ({ children, content, className = "" }) => (
  <div className={`relative group inline-block ${className}`}>
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
      {content}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  </div>
);

/**
 * Empty State Component
 * Displays when no data is available.
 */
export const EmptyState = ({ 
  icon: IconComponent, 
  title, 
  description, 
  action = null 
}) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    {IconComponent && (
      <div className="mb-4 p-4 bg-slate-100 rounded-full">
        <IconComponent size={32} className="text-slate-400" />
      </div>
    )}
    <h3 className="text-lg font-semibold text-slate-700 mb-2">{title}</h3>
    {description && (
      <p className="text-sm text-slate-500 max-w-sm mb-4">{description}</p>
    )}
    {action}
  </div>
);

/**
 * Loading Spinner Component
 */
export const LoadingSpinner = ({ size = 'default', className = '' }) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    default: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  return (
    <div className={`animate-spin rounded-full border-2 border-slate-200 border-t-blue-600 ${sizeClasses[size]} ${className}`} />
  );
};

/**
 * Page Loading Component
 */
export const PageLoader = ({ message = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px]">
    <LoadingSpinner size="large" />
    <p className="mt-4 text-slate-500">{message}</p>
  </div>
);
